// Title Case for the admin panel's labels (the same file as website/src/lib/titleCase.ts, without types): headings, buttons, links, tabs, field labels, table heads.
// "Pick a collection slot" -> "Pick a Collection Slot". The same rules as the customer app
// (customer-app/src/titleCase.ts): only a short text that is not a sentence is changed, only words
// written all in small letters, small joining words stay small inside a label, a measure after a
// number stays small. Sentences, codes and what people typed stay exactly as written.
//
// It runs on the page itself (startTitleCase in index.js): every label element on screen, and every
// one that appears or changes later, so pages write their words in any case. data-raw on an element
// keeps its words as written; data-tc makes any element a label (badges, small captions).
export const TITLE_CASE = { on: true, everyWord: false, maxWords: 8 };

const MINOR = new Set(['a', 'an', 'the', 'and', 'but', 'or', 'nor', 'for', 'at', 'by', 'in', 'of', 'on', 'to', 'per', 'via', 'vs', 'as']);
const UNITS = new Set(['km', 'm', 'cm', 'mm', 'kg', 'g', 'mg', 'ml', 'l', 'y', 'am', 'pm', 'x']);
const MARKS = /^([("'“‘[]*)(.*?)([)\]"'”’,:;?!…]*)$/;
const WORD = /^[a-z][a-z'’]*$/;
const COMPOUND = /^[A-Za-z0-9'’]+(?:-[A-Za-z0-9'’]+)+$/;
const NUMBER = /^[~≈+\-₹$]?\d[\d,.:/–-]*%?$/;
const BREAK = /^[·•|–—→-]+$/;

const cap = (w) => w.charAt(0).toUpperCase() + w.slice(1);
const minor = (w) => MINOR.has(w) && !TITLE_CASE.everyWord;
const compound = (w) => w.split('-').map((part, i) => (WORD.test(part) && (i === 0 || !minor(part)) ? cap(part) : part)).join('-');

export function isLabel(text) {
  const t = text.trim();
  if (!t || !/[a-z]/.test(t)) return false;
  if (/[.!]$/.test(t) && !/(\.\.\.|…)$/.test(t)) return false;
  if (/[.!?]\s+\S/.test(t)) return false;
  return t.split(/\s+/).filter((w) => /[A-Za-z]/.test(w)).length <= TITLE_CASE.maxWords;
}

export function titleWords(text, last = true, first = true) {
  const parts = text.split(/(\s+)/);
  const at = [];
  parts.forEach((p, i) => { if (p.trim()) at.push(i); });
  at.forEach((i, n) => {
    const p = parts[i];
    const prev = n > 0 ? parts[at[n - 1]] : '';
    const next = n < at.length - 1 ? parts[at[n + 1]] : '';
    const m = MARKS.exec(p);
    if (!m) return;
    const [, open, word, close] = m;
    if (COMPOUND.test(word)) { parts[i] = open + compound(word) + close; return; }
    if (!WORD.test(word)) return;
    if (UNITS.has(word) && NUMBER.test(prev)) return;
    const opens = n === 0 ? first : BREAK.test(prev) || /:$/.test(prev);
    const closes = n === at.length - 1 ? last : BREAK.test(next);
    if (minor(word) && !opens && !closes) return;
    parts[i] = open + cap(word) + close;
  });
  return parts.join('');
}

export const titleCase = (text) => (TITLE_CASE.on && isLabel(text) ? titleWords(text) : text);

// ---------------------------------------------------------------- on the page
const PICK = 'h1,h2,h3,h4,h5,h6,button,a,label,th,dt,legend,summary,option,[role="tab"],[role="menuitem"],[role="menuitemcheckbox"],[role="option"],[data-tc]';
// inside these the words are data or code, never relabelled
const SKIP = 'input,textarea,[contenteditable="true"],code,pre,kbd,script,style,svg,.font-mono,[data-raw]';

function labelOf(node) {
  const el = node.parentElement?.closest(PICK) || null;
  if (!el || node.parentElement?.closest(SKIP)) return null;
  if (el.matches('a') && el.closest('td')) return null; // a name or a number linked in a table row
  return el;
}

function relabel(el) {
  if (el.closest(SKIP)) return;
  const nodes = [];
  const walk = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  for (let n = walk.nextNode(); n; n = walk.nextNode()) if (labelOf(n) === el) nodes.push(n);
  if (!nodes.length) return;
  if (!isLabel(nodes.map((n) => n.data).join(''))) return;
  const written = nodes.map((n) => n.data.trim() !== '');
  const firstAt = written.indexOf(true); const lastAt = written.lastIndexOf(true);
  nodes.forEach((n, k) => {
    if (!written[k]) return;
    const next = titleWords(n.data, k === lastAt, k === firstAt);
    if (next !== n.data) n.data = next;
  });
}
function placeholder(el) {
  const p = el.getAttribute('placeholder');
  if (p && !el.closest('[data-raw]')) { const next = titleCase(p); if (next !== p) el.setAttribute('placeholder', next); }
}

// Watches `root` and keeps every label on it in Title Case. Safe to call once at start-up.
export function startTitleCase(root = document.body) {
  if (!TITLE_CASE.on || typeof MutationObserver === 'undefined') return;
  const dirty = new Set(); let queued = false;
  const flush = () => {
    queued = false;
    const list = [...dirty]; dirty.clear();
    list.forEach((el) => { if (el.isConnected) { if (el.hasAttribute('placeholder')) placeholder(el); else relabel(el); } });
  };
  const mark = (el) => { if (!el) return; dirty.add(el); if (!queued) { queued = true; queueMicrotask(flush); } };
  const scan = (el) => {
    if (el.matches(PICK)) mark(el);
    if (el.matches('input[placeholder],textarea[placeholder]')) mark(el);
    el.querySelectorAll(PICK).forEach(mark);
    el.querySelectorAll('input[placeholder],textarea[placeholder]').forEach(mark);
  };
  scan(root);
  new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.type === 'characterData') mark(labelOf(m.target));
      else if (m.type === 'attributes') mark(m.target);
      else {
        m.addedNodes.forEach((n) => { if (n.nodeType === 1) { scan(n); mark(labelOf(n)); } else if (n.nodeType === 3) mark(labelOf(n)); });
      }
    }
  }).observe(root, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['placeholder'] });
}
